import React from "react"
import {connect} from "react-redux"
import {bindActionCreators} from "redux"
import loadable from "@loadable/component"

import {getSvvdCombinedWithName, iodtUtcToTimezoneDateTime24Hr, keys} from "../../utils/util"
import InstructionBox from "./InstructionBox"
import {boldTextStyle, colors, normalTextStyle} from "../../utils/styles"
import Disclaimer from "../Disclaimer"
import {clearData, searchTodo, setSearchTerm} from "../../state/todolist/actions"
import SupportedTerminals from "./SupportedTerminals"
import MaintenancePage from "./MaintenancePage"
import OtherTerminals from "../OtherTerminals"

const textFieldLoader = loadable(
    () => import(/* webpackChunkName: "textField" */"@material/react-text-field"))
let TextField, Input, HelperText
let moment
const momentLoader = loadable(() => import(/* webpackChunkName: "momentTimezone" */ "moment-timezone"))

const vesselLabelStyle = {
  fontSize: 16,
  color: colors.grayDark,
  letterSpacing: 0.4
}

const vesselValueStyle = {
  fontSize: 16,
  color: colors.offBlack,
  marginRight: 28
}

export class ToDoListSearch extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loaded: false,
      showInstructions: true,
      displayDowntime: null
    }
    textFieldLoader.load().then((module) => {
      TextField = module.default
      Input = module.Input
      HelperText = module.HelperText

      momentLoader.load().then((module) => {
        moment = module.default
        this.setState({loaded: true})
      })
    })
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const {data} = nextProps
    let showInstructions = true
    if (data && data.length > 0) {
      showInstructions = false
    }
    if (showInstructions !== prevState.showInstructions) {
      return {
        showInstructions: showInstructions
      }
    }
    return null
  }

  componentDidUpdate() {
    if (this.state.loaded && this.state.displayDowntime === null) {
      if (this.props.startMoment && this.props.endMoment) {
        const now = moment()
        const startCheck = moment(this.props.startMoment).subtract(48, 'hours')
        let displayDowntime = false
        if (now.isBetween(startCheck, this.props.endMoment)) {
          displayDowntime = true
        }
        this.setState({displayDowntime: displayDowntime})
      }
    }
  }

  handleSearchClick() {
    if (this.state.displayDowntime) {
      const now = moment()
      if (now.isBetween(this.props.startMoment, this.props.endMoment) && !this.props.isDefaultWeeklyDowntime) {
        this.setState({displayMaintenance: true})
        return
      }
    }
    this.setState({displayMaintenance: false})
    this.props.actions.searchTodo(this.props.searchTerm)
  }

  handleKeyDown(canSearch, event) {
    if (canSearch && event.keyCode === keys.enter) {
      this.handleSearchClick()
    }
  }

  handleSearchTermChanged(event) {
    this.props.actions.clearData()
    this.props.actions.setSearchTerm(event.target.value.trim())
  }

  getDowntimeMessage() {
    if (!this.state.displayDowntime) {
      return null
    }

    const format = "dddd MM/DD/YYYY hh:mm A z"
    const {startMoment, endMoment} = this.props

    const maintDates = startMoment.tz("America/New_york").format(format) + " until "
        + endMoment.tz("America/New_york").format(format)

    return (
        <div style={{paddingTop: 10, paddingLeft: 20, fontSize: 16}}>
          Our next system maintenance is scheduled on <strong>{maintDates}</strong>.
          Transactions <strong>will not</strong> be available during this period.
        </div>
    )
  }

  render() {
    if (!Input || !moment) {
      return null
    }
    if (this.state.displayMaintenance) {
      return <MaintenancePage/>
    }

    let validInput = false
    const searchTerm = this.props.searchTerm
    if (searchTerm && searchTerm.length >= 3 && searchTerm.length <= 14) {
      validInput = true
    }

    let searchHelperText = null
    let message = null
    let noSearchTermFound = false

    if (this.props.messages && this.props.messages.length > 0) {
      message = this.props.messages[0]
      if (message !== "System Unavailable") {
        noSearchTermFound = true
        searchHelperText = <HelperText className="todo-helper-error" validation={true}>{message}</HelperText>
      }
    }

    const firstCntr = this.props.data?.[0]
    const svvdName = firstCntr ? getSvvdCombinedWithName(firstCntr.svvd, firstCntr.vessel) : null
    const vesselArrival = firstCntr ? iodtUtcToTimezoneDateTime24Hr(firstCntr.vslArrivalUtc,
        firstCntr.pickupTz,
        false) : null
    const canSearch = validInput && !noSearchTermFound

    return (
        <div style={{display: 'flex', flexDirection: 'column'}}>
          {this.getDowntimeMessage()}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: 20,
            paddingTop: 25,
          }}>
            {/*extra div needed to properly align red helper text*/}
            <div>
              <TextField
                  label="Container / Bill of Lading"
                  className="searchInput"
                  style={{width: 200}}
                  helperText={searchHelperText}
              >
                <Input
                    id={'searchInput'}
                    style={{fontSize: '16px', padding: '20px 0px 6px 7px'}}
                    value={this.props.searchTerm}
                    isValid={canSearch}
                    onChange={this.handleSearchTermChanged.bind(this)}
                    onKeyDown={this.handleKeyDown.bind(this, canSearch)}
                    disabled={this.props.searching}
                />
              </TextField>
            </div>
            {/*flex gap not supported by 2021 safari*/}
            <div style={{width: 20}}/>
            {(this.props.searching || (this.props.data && this.state.gridLoading))
                ? <img alt="..." src="images/loading2.gif" style={{height: 44, marginLeft: 35}}/>
                : <button className="blue-button-round"
                          style={{height: 44, minWidth: 120}}
                          disabled={!validInput}
                          onClick={this.handleSearchClick.bind(this)}
                >{this.props.isLocked ? 'Refresh' : 'Search'}</button>}
          </div>
          {this.getMessage(this.props.data)}
          {firstCntr &&
              <div style={{paddingLeft: 20}}>
                <span style={vesselLabelStyle}>{'Vessel: '}</span>
                <span style={vesselValueStyle}>{svvdName}</span>
                <span style={vesselLabelStyle}>{'Vessel Arrival: '}</span>
                <span style={vesselValueStyle}>{vesselArrival}</span>
                <span style={vesselLabelStyle}>{'Pickup Location: '}</span>
                <span style={vesselValueStyle}>{firstCntr.pickupLocation}</span>
              </div>}
          {this.state.showInstructions &&
              <InstructionBox>
                <span style={normalTextStyle}>This platform supports demurrage charge payment for import shipments moving through the following:</span>
                <SupportedTerminals
                    style={{...boldTextStyle, lineHeight: 1.5}}
                    userProfile={this.props.userProfile}
                />
                <Disclaimer userProfile={this.props.userProfile}/>
                <OtherTerminals userProfile={this.props.userProfile}/>
              </InstructionBox>
          }
        </div>
    )
  }

  getMessage(data) {
    if (!data?.length) {
      return null
    }
    return <div style={{
      padding: 20,
      fontSize: 16,
      color: colors.blue,
      fontWeight: 700
    }}>
      {this.props.isLocked
          ? <>This B/L is locked while processing recent changes to pick-up dates and will be available soon.
            Click the &apos;Refresh&apos; button to check.</>
          : data.length === 1
              ? <>Click the appropriate action button to continue.</>
              : <>Click on the checkbox to select your container(s) then click the appropriate action button
                to continue.</>}
    </div>
  }
}

function mapStateToProps(state) {
  return {
    data: state.todolist.data,
    messages: state.todolist.messages,
    searchTerm: state.todolist.searchTerm,
    searching: state.todolist.searching,
    startMoment: state.app.startMoment,
    endMoment: state.app.endMoment,
    isDefaultWeeklyDowntime: state.app.isDefaultWeeklyDowntime,
    userProfile: state.userProfile
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
        {searchTodo, setSearchTerm, clearData}, dispatch), dispatch
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ToDoListSearch)
